/**
 * App shell — the top-bar search (1d): pages AND patients in one box.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: the app shell plan (2026-10-08) §2 — resume block in Documents/Admin-data-table-redesign.md §0
 *
 *   tokenize / matchesWords  the design's matcher: every typed word is the START of some word in
 *                            the text ("rev" → Reviews, "unsch tx" → Unscheduled treatment)
 *   buildPages               the "Go to" list: visible apps, visible follow-up lists, account pages
 *   searchPages              the first `max` pages that match
 *   buildCommands            the Tasks commands (New task, Task calendar …) — pages whose `to` has a query
 *   searchTasks / taskSub    task rows: matching, and the "Overdue Oct 12 · Deepa" second line
 *   paletteState             what the open search shows (pages, commands, patients or recently viewed,
 *                            tasks, the "no results" line, and what Enter opens)
 *   syncLabel                the footer's "Patient list synced N min ago" (in-memory, per session)
 *
 * Patient matching itself stays in src/utils/patientSearch.js (searchPatientsWithCount) — the shell passes
 * its results in; nothing here reads patient fields.
 */

/**
 * The query's words: lower-cased, split on spaces, commas and apostrophes (' and ’ — the same places
 * matchesWords splits the TEXT, so typing "everyone's" finds "Everyone’s tasks"), blanks dropped.
 */
export function tokenize(q) {
  if (typeof q !== 'string') return [];
  return q.trim().toLowerCase().split(/[\s,’']+/).filter(Boolean);
}

/**
 * True when every token is a prefix of some word of `text` (case-insensitive). Text words split
 * on spaces, commas, apostrophes (' and ’) and slashes. `tokens` may be an array or a raw query.
 * No tokens ⇒ true (every() of nothing), as in the design.
 */
export function matchesWords(text, tokens) {
  const tk = Array.isArray(tokens) ? tokens.map(t => String(t).toLowerCase()).filter(Boolean) : tokenize(tokens);
  const words = String(text == null ? '' : text).toLowerCase().split(/[\s,’'/]+/);
  return tk.every(k => words.some(w => w.indexOf(k) === 0));
}

/** Follow-up list icons (design LISTS) — must equal apps.js FOLLOWUP_SUBJECTS icons (tested). */
export const LIST_ICONS = {
  overdueRecall: 'clock',
  unscheduledTreatment: 'list',
  aging: 'dollar',
  brokenAppointments: 'calx',
};

/**
 * Every page the search can open, in the design's order: apps, follow-up lists, Settings,
 * My account. Each: `{ id, kind: 'app'|'list'|'account', label, sub, icon, alias, to }`.
 * @param {Array}   apps          visibleApps(NAV_APPS, …) — registry order, rows without `to` skipped
 * @param {Array}   followupTabs  pageChrome.visibleTabs(can) — `{ key, label, to }`
 * @param {boolean} canMyAccount  show "My account"
 * @param {boolean} canSettings   show "Settings"
 * @param {Object}  aliases       apps.js PAGE_ALIASES (extra words per id / list key)
 */
export function buildPages({ apps, followupTabs, canMyAccount, canSettings, aliases } = {}) {
  const al = aliases || {};
  const aliasFor = id => (typeof al[id] === 'string' ? al[id] : '');
  const pages = [];
  (apps || []).forEach((a) => {
    if (!a || !a.id || !a.to) return;
    pages.push({ id: a.id, kind: 'app', label: a.label, sub: a.description || '', icon: a.icon, alias: aliasFor(a.id), to: a.to });
  });
  (followupTabs || []).forEach((t) => {
    if (!t || !t.key || !t.to) return;
    pages.push({ id: t.key, kind: 'list', label: t.label, sub: 'Follow-ups', icon: LIST_ICONS[t.key] || 'list', alias: aliasFor(t.key), to: t.to });
  });
  if (canSettings === true) {
    pages.push({ id: 'settings', kind: 'account', label: 'Settings', sub: 'Account', icon: 'gear', alias: aliasFor('settings'), to: { path: '/app/settings' } });
  }
  if (canMyAccount === true) {
    pages.push({ id: 'myAccount', kind: 'account', label: 'My account', sub: 'Account', icon: 'user', alias: aliasFor('myAccount'), to: { path: '/app/my-account' } });
  }
  return pages;
}

/** The first `max` pages whose label + alias match. A blank query matches nothing. */
export function searchPages(pages, q, max = 4) {
  const tk = tokenize(q);
  if (!tk.length) return [];
  return (pages || []).filter(p => p && matchesWords(`${p.label} ${p.alias || ''}`, tk)).slice(0, max);
}

/**
 * The palette's Tasks commands — each a "page" whose `to` carries a query the Tasks page reads
 * (src/utils/tasksLayoutGate.js routeIntent: ?compose=1 · ?view=<key> · ?layout=<…>; ?screen=reports is the
 * page's own). Same shape as buildPages rows, so one `pick-page` navigates them. Only when the shell shows
 * Tasks (`canTasks`); the layouts reach only a Super Admin — the shell is Super-Admin-only today, and
 * routeIntent refuses a layout for anyone else anyway.
 * Each: `{ id, kind: 'command', label, sub, icon, alias, to }`.
 */
export function buildCommands({ canTasks } = {}) {
  if (canTasks !== true) return [];
  const t = (id, label, icon, alias, query) => ({
    id: `cmd-${id}`, kind: 'command', label, sub: 'Tasks', icon, alias: `${alias} tasks`, to: { path: '/app/todo', query },
  });
  return [
    t('new-task', 'New task', 'plusC', 'add create todo reminder', { compose: '1' }),
    t('calendar', 'Task calendar', 'cal', 'month week schedule due dates', { layout: 'calendar' }),
    t('workload', 'Task workload', 'gauge', 'team busy who overloaded overdue', { layout: 'workload' }),
    t('list', 'Task list', 'list', 'default back classic', { layout: 'list' }),
    t('my-day', 'My day', 'checkSq', 'today due', { layout: 'list', view: 'myDay' }),
    t('assigned', 'Tasks assigned to me', 'user', 'mine', { layout: 'list', view: 'assignedToMe' }),
    t('created', 'Tasks I created', 'user', 'mine created by me', { layout: 'list', view: 'createdByMe' }),
    t('upcoming', 'Upcoming tasks', 'clock', 'next week soon', { layout: 'list', view: 'upcoming' }),
    t('everyone', 'Everyone’s tasks', 'users', 'office team all', { layout: 'list', view: 'everyone' }),
    t('reports', 'Task reports', 'chart', 'stats numbers', { screen: 'reports' }),
  ];
}

/**
 * The first `max` task rows whose text (+ `hay`, extra words such as a tagged patient's name) match every
 * typed word's start. `entries`: `{ id, text, sub, hay? }` in the order to show them. Blank query ⇒ none.
 */
export function searchTasks(entries, q, max = 5) {
  const tk = tokenize(q);
  if (!tk.length) return [];
  return (entries || []).filter(e => e && e.id != null && matchesWords(`${e.text || ''} ${e.hay || ''}`, tk)).slice(0, max);
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A task row's second line: "Overdue Oct 12 · Deepa" / "Due today · Ana, Ben" / "Due Oct 20 · +3" / "No due date".
 * dueDay / today: 'YYYY-MM-DD'; names: assignee names (blank ones skipped), two shown then "+N".
 */
export function taskSub(dueDay, today, names) {
  const re = /^(\d{4})-(\d{2})-(\d{2})$/;
  const m = re.exec(typeof dueDay === 'string' ? dueDay : '');
  let when = 'No due date';
  if (m) {
    const md = `${MONTHS_SHORT[Number(m[2]) - 1] || ''} ${Number(m[3])}`.trim();
    if (re.test(today) && dueDay === today) when = 'Due today';
    else if (re.test(today) && dueDay < today) when = `Overdue ${md}`;
    else when = `Due ${md}`;
  }
  const who = (Array.isArray(names) ? names : []).filter(n => typeof n === 'string' && n.trim());
  if (!who.length) return when;
  const shown = who.slice(0, 2).join(', ');
  return `${when} · ${who.length > 2 ? `${shown} +${who.length - 2}` : shown}`;
}

/**
 * What the open search shows.
 * @param {string} q         the typed text
 * @param {Array}  pages     buildPages(…)
 * @param {Array}  patients  searchPatientsWithCount(index, q, 20).patients for this q
 * @param {number} [patientTotal]  searchPatientsWithCount(…).total — every patient that matches, before the cap
 * @param {Array}  recent    recently viewed patients (resolved from getRecentPatNums)
 * @param {Array}  commands  buildCommands(…) — matched on label + alias, like pages
 * @param {Array}  tasks     task rows `{ id, text, sub, hay? }` — matched by searchTasks
 * @returns {{ pages, commands, patients, morePatients, tasks, patientTitle, none, first }} — screen order is
 *          pages, commands, patients, tasks. `morePatients` = patientTotal − the patients shown (never
 *          below 0), for the "+N more" hint; 0 on a blank query (Recently viewed) or when no total is given.
 *          `first` is what Enter opens: the top page, else the top command,
 *          else (with a query) the top patient, else the top task, else null. Tasks and commands show
 *          only for a typed query.
 */
export function paletteState({
  q, pages, patients, patientTotal, recent, commands, tasks, maxPages = 4, maxPatients = 20, maxRecent = 5, maxCommands = 4, maxTasks = 5,
} = {}) {
  const has = tokenize(q).length > 0;
  const pg = has ? searchPages(pages, q, maxPages) : [];
  const cm = has ? searchPages(commands, q, maxCommands) : [];
  const pt = has ? (patients || []).slice(0, maxPatients) : (recent || []).slice(0, maxRecent);
  const tk = has ? searchTasks(tasks, q, maxTasks) : [];
  const more = has && Number.isFinite(patientTotal) ? Math.max(0, patientTotal - pt.length) : 0;
  let first = null;
  if (pg.length) first = { type: 'page', page: pg[0] };
  else if (cm.length) first = { type: 'command', page: cm[0] };
  else if (has && pt.length) first = { type: 'patient', patient: pt[0] };
  else if (tk.length) first = { type: 'task', task: tk[0] };
  return {
    pages: pg,
    commands: cm,
    patients: pt,
    morePatients: more,
    tasks: tk,
    patientTitle: has ? 'Patients' : 'Recently viewed',
    none: has && !pg.length && !cm.length && !pt.length && !tk.length,
    first,
  };
}

function toMs(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  return NaN;
}

/**
 * The search footer. Refreshing ⇒ "Syncing with Open Dental…"; under a minute (or a clock that
 * runs backwards) ⇒ "Synced just now"; then minutes, hours, days. No time known ⇒ ''.
 */
export function syncLabel(lastSyncedAt, now, refreshing) {
  if (refreshing === true) return 'Syncing with Open Dental…';
  const then = toMs(lastSyncedAt);
  const at = toMs(now);
  if (!Number.isFinite(then) || !Number.isFinite(at)) return '';
  const secs = Math.floor((at - then) / 1000);
  if (secs < 60) return 'Synced just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `Patient list synced ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Patient list synced ${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `Patient list synced ${days} day${days === 1 ? '' : 's'} ago`;
}
